import { WorkflowRule } from '../types/workflowRules';

/**
 * Mock workflow rules for development and testing
 */
export const mockWorkflowRules: WorkflowRule[] = [
  {
    id: 'wfr_001',
    name: 'Lead Auto-Assignment',
    description: 'Automatically assigns qualified leads to the Sales Manager',
    objectApiName: 'Lead',
    isActive: true,
    triggerType: 'onCreateAndUpdate',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_001',
          fieldApiName: 'status',
          operator: 'equals',
          value: 'Qualified',
          fieldType: 'picklist'
        }
      ]
    },
    actions: [
      {
        id: 'act_001',
        type: 'fieldUpdate',
        order: 1,
        config: {
          fieldApiName: 'owner',
          newValue: 'user_sales_manager'
        }
      },
      {
        id: 'act_002',
        type: 'emailAlert',
        order: 2,
        config: {
          templateId: 'template_lead_assignment',
          recipients: [
            { type: 'user', userId: 'user_sales_manager' },
            { type: 'recordOwner' }
          ]
        }
      }
    ],
    createdDate: '2025-11-01T10:00:00Z',
    createdBy: 'user_admin',
    lastModified: '2025-11-15T14:30:00Z',
    lastModifiedBy: 'user_admin',
    executionCount: 47,
    lastExecuted: '2025-11-18T09:15:00Z'
  },

  {
    id: 'wfr_002',
    name: 'Case Escalation for Urgent Issues',
    description: 'Escalates urgent cases to department heads if not completed within SLA',
    objectApiName: 'Case',
    isActive: true,
    triggerType: 'onCreateAndUpdate',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_002',
          fieldApiName: 'priority',
          operator: 'equals',
          value: 'Urgent',
          fieldType: 'picklist'
        },
        {
          id: 'cond_003',
          fieldApiName: 'status',
          operator: 'notEquals',
          value: 'Closed',
          fieldType: 'picklist'
        }
      ]
    },
    actions: [
      {
        id: 'act_003',
        type: 'emailAlert',
        order: 1,
        config: {
          templateId: 'template_case_escalation',
          recipients: [
            { type: 'recordOwner' },
            { type: 'role', roleId: 'role_cs_supervisor' }
          ]
        }
      },
      {
        id: 'act_004',
        type: 'fieldUpdate',
        order: 2,
        config: {
          fieldApiName: 'status',
          newValue: 'Escalated'
        }
      },
      {
        id: 'act_005',
        type: 'task',
        order: 3,
        config: {
          subject: 'Review Escalated Case',
          description: 'This urgent case requires immediate attention',
          assignedTo: 'role_department_head',
          dueDate: { type: 'relative', days: 1 },
          priority: 'High',
          status: 'Not Started',
          relatedTo: 'triggerRecord'
        }
      }
    ],
    createdDate: '2025-10-15T08:00:00Z',
    createdBy: 'user_admin',
    lastModified: '2025-11-10T11:20:00Z',
    lastModifiedBy: 'user_cs_supervisor',
    executionCount: 23,
    lastExecuted: '2025-11-17T16:45:00Z'
  },

  {
    id: 'wfr_003',
    name: 'Client Welcome Email',
    description: 'Sends welcome email when client is enrolled in a product',
    objectApiName: 'Client',
    isActive: false, // Inactive for now
    triggerType: 'onCreateAndUpdate',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_004',
          fieldApiName: 'status',
          operator: 'equals',
          value: 'Active',
          fieldType: 'picklist'
        },
        {
          id: 'cond_005',
          fieldApiName: 'products',
          operator: 'isNotEmpty',
          value: null,
          fieldType: 'multiselect'
        }
      ]
    },
    actions: [
      {
        id: 'act_006',
        type: 'emailAlert',
        order: 1,
        config: {
          templateId: 'template_client_welcome',
          recipients: [
            { type: 'email', email: '{Client.Email}' }
          ],
          additionalEmails: ['support@regalcredit.com']
        }
      }
    ],
    createdDate: '2025-11-05T12:00:00Z',
    createdBy: 'user_admin',
    lastModified: '2025-11-05T12:00:00Z',
    lastModifiedBy: 'user_admin',
    executionCount: 0
  },

  {
    id: 'wfr_004',
    name: 'Task Overdue Notification',
    description: 'Notifies assigned user when task becomes overdue',
    objectApiName: 'Task',
    isActive: true,
    triggerType: 'onUpdateOnly',
    evaluationCriteria: {
      operator: 'AND',
      conditions: [
        {
          id: 'cond_006',
          fieldApiName: 'status',
          operator: 'notEquals',
          value: 'Completed',
          fieldType: 'picklist'
        },
        {
          id: 'cond_007',
          fieldApiName: 'dueDate',
          operator: 'lessThan',
          value: 'TODAY',
          fieldType: 'date'
        }
      ]
    },
    actions: [
      {
        id: 'act_007',
        type: 'notification',
        order: 1,
        config: {
          message: 'Task "{Task.Subject}" is now overdue. Please complete it as soon as possible.',
          recipients: [
            { type: 'recordOwner' }
          ],
          priority: 'High',
          actionUrl: '/tasks/{Task.Id}'
        }
      },
      {
        id: 'act_008',
        type: 'fieldUpdate',
        order: 2,
        config: {
          fieldApiName: 'priority',
          newValue: 'High'
        }
      }
    ],
    createdDate: '2025-10-20T09:30:00Z',
    createdBy: 'user_admin',
    lastModified: '2025-10-20T09:30:00Z',
    lastModifiedBy: 'user_admin',
    executionCount: 15,
    lastExecuted: '2025-11-18T08:00:00Z'
  }
];

/**
 * Initialize mock workflow rules in localStorage
 */
export function initializeMockWorkflowRules(): void {
  const existing = localStorage.getItem('crm_workflow_rules');

  if (!existing) {
    localStorage.setItem('crm_workflow_rules', JSON.stringify(mockWorkflowRules));
    console.log('✅ Mock workflow rules initialized:', mockWorkflowRules.length);
  }
}
